import type { loader as rootLoader } from "../../root";
import type { loader as followMoreLoader } from "../user.$userId.follow-load/route";
import {
  Link,
  useFetcher,
  useLoaderData,
  useParams,
  useRouteLoaderData,
} from "@remix-run/react";
import clsx from "clsx";
import type { FunctionComponent } from "react";
import {
  useCallback,
  useEffect,
  useState,
} from "react";
import type { loader } from "./route";
import type { SerializeFrom } from "@remix-run/cloudflare";
import { buttonBase } from "~/components/Common/FormStyle";
import Modal from "~/components/Common/Modal";
import { generateFileURL } from "~/utils/file/generateFileURL";
import { convertName } from "~/utils/convertName";

interface FollowUsersProps {
  onClose: () => void;
  isFollow: boolean;
}

type LoaderReturnType = SerializeFrom<
  typeof followMoreLoader
>;

type followCursor = NonNullable<
  LoaderReturnType["followCursor"]
>;

export const FollowUsers: FunctionComponent<
  FollowUsersProps
> = ({ onClose, isFollow }) => {
  const {
    followCursor: _followCursor,
    followerCursor: _followerCursor,
  } = useLoaderData<typeof loader>();

  const param = useParams();
  const fetcher = useFetcher<LoaderReturnType>();
  const [followUsers, setFollowUsers] =
    useState<followCursor>(
      isFollow ? _followCursor : _followerCursor,
    );
  const nextLoad = useCallback(async () => {
    fetcher.load(
      `/user/${param.userId}/follow-load?nextCursor=${followUsers.nextCursor}&postsPerPage=${followUsers.usersPerPage}&isFollow=${isFollow}`,
    );
  }, [
    fetcher,
    param.userId,
    followUsers.nextCursor,
    followUsers.usersPerPage,
    isFollow,
  ]);

  useEffect(() => {
    // fetcherの更新
    const data = fetcher.data?.followCursor;
    if (!data) return;
    setFollowUsers((prev) => {
      return {
        ...data,
        users: [...prev.users, ...data.users],
      };
    });
  }, [fetcher.data]);

  useEffect(() => {
    // 初回のみ
    setFollowUsers((prev) => {
      return {
        ...prev,
        posts: prev.users.map((el) => {
          const exists = (
            !isFollow
              ? _followerCursor
              : _followCursor
          ).users?.find((e) => e.id === el.id);
          if (exists) {
            return exists;
          } else {
            return el;
          }
        }),
      };
    });
  }, [_followCursor, _followerCursor, isFollow]);

  const root =
    useRouteLoaderData<typeof rootLoader>("root");
  console.log(
    "followUsers.users.length:",
    followUsers.users.length,
  );
  return (
    <Modal
      title={
        isFollow
          ? "フォローしているユーザー"
          : "フォローされているユーザー"
      }
      onDismiss={() => {
        onClose();
      }}
    >
      <div className="modal">
        <div className={clsx("modal-content")}>
          <div
            className={clsx(
              "overflow-scroll",
              "h-400",
            )}
          >
            {followUsers.users.length === 0 && (
              <div
                className={clsx(
                  "h-400",
                  "flex",
                  "items-center",
                  "justify-center",
                )}
              >
                <div
                  className={clsx(
                    "text-center",
                    "text-gray-400",
                    "font-bold",
                    "pb-40",
                  )}
                >
                  ユーザーがいません
                </div>
              </div>
            )}
            {followUsers.users.length > 0 && (
              <div className={clsx()}>
                <div
                  className={clsx(
                    "flex",
                    "mx-auto",
                    "flex-col",
                    "gap-10",
                    "py-30",
                  )}
                >
                  <div className={clsx()}>
                    <div className={clsx()}>
                      <ul
                        className={clsx(
                          "gap-20",
                          "flex",
                          "flex-col",
                          "items-start",
                          "max-md:w-360",
                          "max-md:mr-0",
                        )}
                      >
                        {[
                          ...followUsers.users,
                        ]?.map((el, i) => {
                          return (
                            <li
                              key={i}
                              className={clsx(
                                "w-[90%]",
                              )}
                            >
                              <Link
                                to={`/user/${el.id}`}
                                onClick={() => {
                                  onClose();
                                }}
                                className={clsx(
                                  "grid",
                                  "grid-cols-[auto,1fr]",
                                  "gap-15",
                                  "px-40",
                                )}
                              >
                                <div
                                  className={clsx(
                                    "w-40",
                                    "h-40",
                                    "rounded-full",
                                    "overflow-hidden",
                                    "bg-gray-200",
                                  )}
                                >
                                  {el.iconurl && (
                                    <img
                                      src={generateFileURL(
                                        el.iconurl ??
                                          "",
                                        undefined,
                                        root?.basePath,
                                      )}
                                      alt=""
                                      className={clsx(
                                        "w-full",
                                        "h-full",
                                        "object-cover",
                                      )}
                                    />
                                  )}
                                </div>
                                <div
                                  className={clsx(
                                    "flex",
                                    "flex-col",
                                    "justify-center",
                                  )}
                                >
                                  <div
                                    className={clsx(
                                      "text-14",
                                      "font-bold",
                                      "line-clamp-1",
                                    )}
                                  >
                                    {convertName(
                                      el.name,
                                    )}
                                  </div>
                                </div>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    {followUsers.hasNextPage && (
                      <div
                        className={clsx(
                          "text-center",
                          "mt-60",
                        )}
                      >
                        <button
                          className={clsx(
                            "bg-red",
                            buttonBase({
                              size: "md",
                              round: "full",
                              variant: "default",
                            }),
                          )}
                          onClick={() => {
                            nextLoad();
                          }}
                        >
                          もっと見る
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
